import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TreeNode as TreeNodeType } from '../models/TreeModel';
import { 
  findNodeById,
  canNodeTypeHaveChildren,
} from '../services/treeService';
import { executeContextMenuAction } from '../services/contextMenuService';
import { TreeNode } from './TreeNode';
import { ExplorerToolbar } from './ExplorerToolbar';
import { TreeProvider } from '../context/TreeContext';
import { createTreeSelectionStore } from '../context/treeSelectionStore';
import { DEFAULT_ICON_MAP } from './TreeNode/treeNodeIconMap';

interface ExplorerProps {
  title?: string;
  nodes: TreeNodeType[];
  onNodeClick?: (nodeId: string) => void;
  onRefresh?: (nodeId: string) => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  onAdd?: () => void;
  onFilter?: () => void;
  onCollapseAll?: () => void;
  onCollapseSidebar?: () => void;
  renderCustomActions?: () => React.ReactNode;
  className?: string;
  iconMap?: Record<string, React.ElementType>;
  selectedNodeId?: string;
  onContextMenuAction?: (nodeId: string, actionId: string) => void;
  onToggleExpand?: (nodeId: string) => void;
  highlightedConnectionIds?: Set<string>;
  highlightedNodeId?: string;
  onNodeHighlightComplete?: (connectionId: string) => void;
  onHighlightedNodeComplete?: () => void;
  onReconnect?: (connectionId: string) => Promise<boolean>;
  /**
   * T12 fix round 1, M-4 — forwarded verbatim into `TreeContext` so every
   * `TreeNode` row reads AI-launcher gating from context instead of each row
   * calling `useCliDetection()` itself. `Explorer`/`TreeProvider` currently
   * has exactly one consumer (Object Explorer), so these two fields are
   * `undefined` (and unused) for any other future caller.
   */
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
}

export function Explorer({
  title,
  nodes,
  onNodeClick,
  onRefresh,
  searchTerm,
  onSearchChange,
  onAdd,
  onFilter,
  onCollapseAll,
  onCollapseSidebar,
  renderCustomActions,
  className = '',
  iconMap,
  selectedNodeId: externalSelectedNodeId,
  onContextMenuAction,
  onToggleExpand,
  highlightedConnectionIds,
  highlightedNodeId,
  onNodeHighlightComplete,
  onHighlightedNodeComplete,
  onReconnect,
  aiCliAvailable,
  aiCliDetectionLoading,
}: ExplorerProps) {
  const selectedNodeId = externalSelectedNodeId;

  // Perf — selection / highlight state is pushed into a per-instance zustand
  // store instead of riding on the `TreeContext` value, so a selection change
  // re-renders only the rows whose own selected-ness flipped rather than every
  // row in the tree. See `treeSelectionStore.ts`. `useLayoutEffect` (not
  // `useEffect`) so the store write lands in the same commit the user sees —
  // the selection highlight must not lag a painted frame behind the click.
  const [selectionStore] = useState(() =>
    createTreeSelectionStore({
      selectedNodeId: externalSelectedNodeId,
      highlightedNodeId,
      highlightedConnectionIds,
    }),
  );

  useLayoutEffect(() => {
    selectionStore.setState({ selectedNodeId, highlightedNodeId, highlightedConnectionIds });
  }, [selectionStore, selectedNodeId, highlightedNodeId, highlightedConnectionIds]);

  // Normalize nodes for rendering (e.g. canHaveChildren)
  const processedNodes = useMemo(() => {
    const processNode = (node: TreeNodeType): TreeNodeType => {
      const children = node.children?.map(child => processNode(child));
      
      // Determine if this node can have children
      const isDynamicParent = canNodeTypeHaveChildren(node.type);
      const hasChildren = children && children.length > 0;
      
      return {
        ...node,
        children,
        canHaveChildren: node.canHaveChildren ?? (isDynamicParent || hasChildren),
      };
    };
    
    return nodes.map(node => processNode(node));
  }, [nodes]);

  // Perf — the `TreeContext` value must keep a STABLE identity across renders,
  // because every one of the (non-virtualized) tree's rows consumes it: a new
  // value object re-renders all of them, which is what `React.memo` on
  // `TreeNode` cannot protect against. The handlers this component receives
  // are NOT stable — several of `DatabaseExplorer`'s `useCallback`s close over
  // per-render values — so instead of forwarding them by identity we keep the
  // latest ones in a ref and hand the tree fixed wrappers that read through it.
  // Only genuinely render-affecting values (`iconMap`, the AI-CLI gating flags,
  // whether a reconnect handler exists at all) stay as context-value deps.
  const latestRef = useRef({
    onNodeClick,
    onToggleExpand,
    onRefresh,
    onContextMenuAction,
    onReconnect,
    onNodeHighlightComplete,
    onHighlightedNodeComplete,
    processedNodes,
  });
  // Written on commit rather than during render: every reader is an event
  // handler, which only ever runs after a commit, so a render React discards
  // must not be allowed to leave its values behind in the ref.
  useLayoutEffect(() => {
    latestRef.current = {
      onNodeClick,
      onToggleExpand,
      onRefresh,
      onContextMenuAction,
      onReconnect,
      onNodeHighlightComplete,
      onHighlightedNodeComplete,
      processedNodes,
    };
  });

  // Handle node selection only (without expanding/collapsing)
  const handleNodeSelect = useCallback((nodeId: string) => {
    latestRef.current.onNodeClick?.(nodeId);
  }, []);

  // Handle node expansion/collapse only (without selecting)
  const handleNodeExpand = useCallback((nodeId: string) => {
    latestRef.current.onToggleExpand?.(nodeId);
  }, []);

  // Handle node refresh for a specific node
  const handleNodeRefresh = useCallback((nodeId: string) => {
    latestRef.current.onRefresh?.(nodeId);
  }, []);

  // Handle toolbar refresh button click - refresh the selected node
  const handleToolbarRefresh = useCallback(() => {
    if (selectedNodeId) {
      handleNodeRefresh(selectedNodeId);
    }
  }, [selectedNodeId, handleNodeRefresh]);

  // Handle context menu actions
  const handleContextMenuAction = useCallback((nodeId: string, actionId: string) => {
    const { onContextMenuAction: handler, processedNodes: currentNodes } = latestRef.current;
    // If parent provides a handler, let it handle everything
    if (handler) {
      handler(nodeId, actionId);
      return;
    }

    // Otherwise, use the generic context menu service for basic actions
    const node = findNodeById(nodeId, currentNodes);
    if (!node) {
      console.error(`Explorer: Could not find node with id ${nodeId} for context menu action ${actionId}`);
      return;
    }

    executeContextMenuAction(actionId, nodeId, {
      onRefresh: handleNodeRefresh,
    }, node.type);
  }, [handleNodeRefresh]);

  const handleNodeHighlightComplete = useCallback((connectionId: string) => {
    latestRef.current.onNodeHighlightComplete?.(connectionId);
  }, []);

  const handleHighlightedNodeComplete = useCallback(() => {
    latestRef.current.onHighlightedNodeComplete?.();
  }, []);

  const handleReconnect = useCallback(
    (connectionId: string) => latestRef.current.onReconnect!(connectionId),
    [],
  );

  // Rows read `onReconnect`'s PRESENCE to decide whether to offer a reconnect
  // affordance, so the wrapper is swapped for `undefined` when no handler is
  // supplied — the identity then only flips when that presence changes.
  const hasReconnect = !!onReconnect;

  const mergedIconMap = useMemo(
    () => ({ ...DEFAULT_ICON_MAP, ...(iconMap ?? {}) }),
    [iconMap],
  );

  const treeContextValue = useMemo(
    () => ({
      selectionStore,
      onNodeExpand: handleNodeExpand,
      onNodeSelect: handleNodeSelect,
      onNodeRefresh: handleNodeRefresh,
      onContextMenuAction: handleContextMenuAction,
      onNodeHighlightComplete: handleNodeHighlightComplete,
      onHighlightedNodeComplete: handleHighlightedNodeComplete,
      onReconnect: hasReconnect ? handleReconnect : undefined,
      iconMap: mergedIconMap,
      aiCliAvailable,
      aiCliDetectionLoading,
    }),
    [
      selectionStore,
      handleNodeExpand,
      handleNodeSelect,
      handleNodeRefresh,
      handleContextMenuAction,
      handleNodeHighlightComplete,
      handleHighlightedNodeComplete,
      handleReconnect,
      hasReconnect,
      mergedIconMap,
      aiCliAvailable,
      aiCliDetectionLoading,
    ]
  );

  return (
    <div className={`h-full w-full flex flex-col bg-background ${className}`} data-testid="database-explorer">
      {/* Toolbar */}
      <ExplorerToolbar
        title={title}
        onRefresh={handleToolbarRefresh}
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        onAdd={onAdd}
        onFilter={onFilter}
        onCollapseAll={onCollapseAll}
        onCollapseSidebar={onCollapseSidebar}
        renderCustomActions={renderCustomActions}
      />
      
      {/* Tree View */}
      <div className="flex-1 overflow-auto bg-background [border:none]" data-testid="explorer-tree-view">
        {processedNodes.length > 0 ? (
          <div className="bg-background [border:none] [box-shadow:none] [outline:none]">
            <TreeProvider value={treeContextValue}>
              {processedNodes.map((node: TreeNodeType) => (
                <TreeNode key={node.id} node={node} depth={0} />
              ))}
            </TreeProvider>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            No items to display
          </div>
        )}
      </div>
    </div>
  );
} 
